/* eslint-disable import/no-unresolved */
/* eslint-disable import/extensions */
import React, { ReactElement, useState, memo, useEffect } from "react";

import { isSafari } from "react-device-detect";
import { Document, Page, pdfjs } from "react-pdf";
import VisibilitySensor from "react-visibility-sensor";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import styled, { css } from "styled-components";

const { version } = pdfjs;
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.min.js`;

const options = {
  cMapUrl: `//cdn.jsdelivr.net/npm/pdfjs-dist@${version}/cmaps/`,
  cMapPacked: false,
};

const DocumentContainer = styled.div<{
  $minWidth: number;
}>(
  ({ $minWidth }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: ${$minWidth}px;
  `
);

const PageContainer = styled.div<{
  $bgColor: string;
  $height: number;
  $marginTop: number;
  $width: number;
}>(
  ({ $bgColor, $height, $marginTop, $width }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: ${$height}px;
    width: ${$width}px;
    margin-top: ${$marginTop}px;
    background-color: ${$bgColor};
  `
);

const PageLoaderContainer = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;
  justify-content: center;
  flex: 1;
`;

export interface Props {
  filename: string;
  hidePages: boolean;
  onLoadSuccess: () => void;
  onPageRenderSuccess: (pageNum: number) => void;
  onVisiblityChange: (pageNum: number, isVisible: boolean) => void;
  pageBackgroundColor: string;
  pageDividerHeight: number;
  pageHeight: number;
  pageLoader: ReactElement;
  pageWidth: number;
  showLoader: boolean;
}

// TODO change name - PDFDocument?
function PDFViewer({
  filename,
  hidePages,
  onLoadSuccess,
  onPageRenderSuccess,
  onVisiblityChange,
  pageBackgroundColor,
  pageDividerHeight,
  pageHeight,
  pageLoader,
  pageWidth,
  showLoader,
}: Props): ReactElement<Props> {
  const [numPages, setNumPages] = useState(0);
  const [renderedPages, setRenderedPages] = useState<number[]>([]);

  useEffect(() => {
    if (isSafari) {
      setRenderedPages((prevState) => [
        ...prevState,
        1,
        2,
        3,
        4,
        5,
        6,
        7,
        8,
        9,
      ]);
    } else {
      setRenderedPages([]);
    }
  }, [hidePages, pageWidth]);

  return (
    <DocumentContainer $minWidth={pageWidth}>
      <Document
        file={filename}
        loading=""
        options={options}
        onLoadSuccess={(loadedPdf) => {
          onLoadSuccess();
          setNumPages(loadedPdf.numPages);
          onVisiblityChange(1, true);
          if (isSafari) {
            setRenderedPages((prevState) => [
              ...prevState,
              1,
              2,
              3,
              4,
              5,
              6,
              7,
              8,
              9,
            ]);
          }
        }}
      >
        {Array.from(Array(numPages)).map((_, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <PageContainer
            key={`page_${index + 1}`}
            $bgColor={pageBackgroundColor}
            $height={pageHeight}
            $marginTop={index > 0 ? pageDividerHeight : 0}
            $width={pageWidth}
          >
            {!hidePages && (
              <VisibilitySensor
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                partialVisibility="bottom"
                onChange={(isVisible): void =>
                  onVisiblityChange(index + 1, isVisible)
                }
              >
                <Page
                  className={
                    !renderedPages.includes(index + 1)
                      ? "pdf-loading"
                      : undefined
                  }
                  height={pageHeight}
                  pageNumber={index + 1}
                  width={pageWidth}
                  onRenderSuccess={
                    isSafari && !showLoader
                      ? undefined
                      : () => {
                          onPageRenderSuccess(index + 1);
                          setRenderedPages((prevState) => [
                            ...prevState,
                            index + 1,
                          ]);
                        }
                  }
                />
              </VisibilitySensor>
            )}

            {(hidePages || !renderedPages.includes(index + 1)) && (
              <PageLoaderContainer>
                {!hidePages && pageLoader}
              </PageLoaderContainer>
            )}
          </PageContainer>
        ))}
      </Document>
    </DocumentContainer>
  );
}

export default memo(PDFViewer);
